import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";

const lineItemInputSchema = z
  .object({
    productNumber: z
      .string()
      .trim()
      .min(1)
      .describe("Vendor catalog / model number."),
    keptFlag: z
      .boolean()
      .optional()
      .describe(
        "True if the row survives cleanup and flows to margin calc. Defaults to true.",
      ),
    dropReason: z
      .string()
      .optional()
      .describe(
        "Reason the row was dropped, e.g. blank_row, missing_catalog, deleted, expired, future_start, ecom_only. Ignored if not one of these values.",
      ),
    rawData: z
      .record(z.string(), z.unknown())
      .optional()
      .describe(
        "All vendor-specific fields (description, price, multiplier, uom, per, validFrom, validTo, pgc, fixedValueDate, etc.).",
      ),
  })
  .passthrough();

export const upsertSpaStep = defineStepConfig({
  type: "persist_spa_from_extraction",
  name: "Upsert SPA",
  description:
    "Upsert a Pricing SPA record from structured extracted fields (e.g. from extract_document_content_v4). " +
    "When lineItems or lineItemsDocument is provided, also replaces all SpaLineItem rows for this SPA in the same transaction.",
  category: "Integration",
  libraryModule: "Pricing",

  inputSchema: z
    .object({
      record: z
        .record(z.string(), z.unknown())
        .describe(
          "Extracted SPA metadata object, usually the extractedFields output from extract_document_content_v4. Expected keys include spaNumber, quoteType, startDate, endDate, currency, customerNumber, and division.",
        ),
      sourceDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
        "Original vendor SPA document. Its entityId is stored as sourceDocumentId on the Spa.",
      ),
      lineItemsDocument: entityOfType(SystemEntity.CORE_DOCUMENT)
        .optional()
        .describe(
          "Normalized canonical CSV from the per-vendor cleanup + normalization nodes. When provided (and lineItems is not), all SpaLineItem rows for this SPA are replaced in the same transaction.",
        ),
      lineItems: z
        .array(lineItemInputSchema)
        .optional()
        .describe(
          "Structured line items to persist directly, as an alternative to lineItemsDocument. When provided, all SpaLineItem rows for this SPA are replaced with these rows (in array order) in the same transaction.",
        ),
      spaRequestId: z
        .string()
        .uuid()
        .optional()
        .describe("FK to the originating SpaRequest, when known."),
      vendor: z
        .string()
        .min(1)
        .describe("Vendor key, e.g. 'hoffman', 'rockwell'."),
    })
    .refine(
      (data) => data.lineItems == null || data.lineItemsDocument == null,
      {
        message:
          "Provide at most one of lineItems or lineItemsDocument, not both.",
        path: ["lineItems"],
      },
    ),

  outputSchema: z.object({
    spa: entityOfType(SystemEntity.PRICING_SPA).describe(
      "The persisted Spa record.",
    ),
    spaId: z.string().uuid().describe("The persisted Spa id."),
    created: z
      .boolean()
      .describe(
        "True when a new Spa row was created; false when an existing row was updated.",
      ),
    spaNumber: z.string().describe("The SPA agreement number."),
    customerNumber: z
      .string()
      .nullable()
      .describe("Customer account number from the SPA header."),
    startDate: z
      .string()
      .nullable()
      .describe("Contract start date (YYYY-MM-DD)."),
    endDate: z.string().nullable().describe("Contract end date (YYYY-MM-DD)."),
    quoteType: z
      .string()
      .nullable()
      .describe("SPA quote type (e.g. ShipAndDebit, StandardSpa)."),
    lineItemCount: z
      .number()
      .int()
      .nullable()
      .describe(
        "Total line item rows inserted. Null when neither lineItemsDocument nor lineItems was provided.",
      ),
    keptCount: z
      .number()
      .int()
      .nullable()
      .describe(
        "Line item rows with keptFlag=true. Null when neither lineItemsDocument nor lineItems was provided.",
      ),
  }),

  configSchema: z.object({
    productNumberColumn: z
      .string()
      .min(1)
      .default("productNumber")
      .describe("CSV column name that maps to productNumber."),
    keptFlagColumn: z
      .string()
      .min(1)
      .default("keptFlag")
      .describe('CSV column name for the kept/dropped flag ("true"/"false").'),
    dropReasonColumn: z
      .string()
      .min(1)
      .default("dropReason")
      .describe("CSV column name for the drop reason string."),
  }),

  emits: [] as const,
});

export type UpsertSpaConfig = z.infer<typeof upsertSpaStep.configSchema>;
